/**
 * The rules that decide which party an ApplicationWebService's bureau report is grafted onto,
 * extracted from getApplicationWithWebServices so they can be tested (AZ-1475).
 *
 * These matter beyond the rule expressions that read the reports. A report that matches nobody is
 * attached to nobody, and every credit rule reading it then silently falls back to its default
 * rather than failing - so a subtle change here shows up as a wrong decision, not an error.
 *
 * The party fields are the API shape's camelCase names: CreditRuleInput is built from
 * applicationIndividualsToApi / applicationCommercialEntitiesToApi, so the graft operates on the
 * parties payload rather than on the legacy projection.
 */

/** The identifying fields of an individual, as the API projection emits them. */
export interface GraftIndividualIdentity {
  givenName?: string | null;
  surname?: string | null;
  dateOfBirth?: string | null;
}

/** The identifying fields of a commercial entity, as the API projection emits them. */
export interface GraftEntityIdentity {
  abn?: string | null;
  acn?: string | null;
}

/** The identifying fields an ApplicationWebService records about who it was pulled for. */
export interface GraftWebServiceIdentity {
  ABN?: string | null;
  ACN?: string | null;
  individualFirstName?: string | null;
  individualLastName?: string | null;
  individualDoB?: string | null;
}

/**
 * A missing name or date is null on both the API projection and the web service, and those must not
 * compare equal: two parties with no date of birth are not the same person. Coercing only the party
 * side to '' keeps that asymmetry, which is what stops a report with no date of birth from grafting
 * onto whichever nameless individual happens to come first. (The legacy projection wrote '' rather
 * than null for an absent name, so the coercion here made both sides behave the same either way.)
 */
export const webServiceMatchesIndividual = (
  individual: GraftIndividualIdentity,
  webService: GraftWebServiceIdentity,
): boolean =>
  (individual.surname ?? '').toUpperCase() === webService.individualLastName?.toUpperCase()
  && (individual.givenName ?? '').toUpperCase() === webService.individualFirstName?.toUpperCase()
  && (individual.dateOfBirth ?? '') === webService.individualDoB;

/**
 * Both numbers must agree, but either may be absent on both sides - a sole trader has no ACN, and a
 * report pulled for one records none. Absent on one side only is a mismatch.
 */
export const webServiceMatchesEntity = (
  entity: GraftEntityIdentity,
  webService: GraftWebServiceIdentity,
): boolean =>
  ((!entity.acn && !webService.ACN) || (!!entity.acn && !!webService.ACN && entity.acn === webService.ACN))
  && ((!entity.abn && !webService.ABN) || (!!entity.abn && !!webService.ABN && entity.abn === webService.ABN));
